import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Users, DollarSign, Printer, TrendingUp, Activity } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const Stats = () => {
  const [stats, setStats] = useState({ totalProducts: 0, totalSales: 0, ordersCount: 0 });
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [rawOrders, setRawOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resProd, resOrd] = await Promise.all([
        axios.get('https://my-pharmacy-api.onrender.com/api/products'),
        axios.get('https://my-pharmacy-api.onrender.com/api/orders')
      ]);
      
      const orders = resOrd.data || [];
      setRawOrders(orders);
      const paidOrders = orders.filter(o => o.payment_status === 'تم الدفع');
      const total = paidOrders.reduce((sum, o) => sum + Number(o.total_price_syp || 0), 0);

      setStats({
        totalProducts: resProd.data?.length || 0,
        totalSales: total,
        ordersCount: orders.length
      });

      const grouped = paidOrders.reduce((acc, order) => {
        const date = new Date(order.created_at).toLocaleDateString('ar-SY', { day: 'numeric', month: 'short' });
        acc[date] = (acc[date] || 0) + Number(order.total_price_syp || 0);
        return acc;
      }, {});

      setChartData({
        labels: Object.keys(grouped),
        datasets: [{
          fill: true,
          label: 'المبيعات اليومية',
          data: Object.values(grouped),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
        }]
      });
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // دالة الطباعة السحرية لدعم العربية
  const handlePrint = () => {
    window.print();
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { grid: { color: 'rgba(156, 163, 175, 0.1)' }, ticks: { color: '#9ca3af' } },
      x: { grid: { display: false }, ticks: { color: '#9ca3af' } }
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen dark:bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
    </div>
  );

  return (
    <div dir="rtl" className="min-h-screen bg-[#f8fafc] dark:bg-[#0f172a] p-4 md:p-8 transition-colors duration-500">
      
      {/* CSS للتحكم في شكل الطباعة */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          .print-container { padding: 0 !important; margin: 0 !important; width: 100% !important; }
          .card-print { border: 1px solid #eee !important; box-shadow: none !important; break-inside: avoid; }
        }
      `}} />

      <div className="print-container max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div className="space-y-1">
            <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">إحصائيات صيدلية ميسون</h1>
            <p className="text-gray-500 dark:text-blue-400 font-bold flex items-center gap-2">
              <Activity size={18} /> التقرير المالي المحدث بتاريخ {new Date().toLocaleDateString('ar-SY')}
            </p>
          </div>
          
          <button 
            onClick={handlePrint}
            className="no-print group flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-[24px] font-black shadow-xl shadow-blue-200 dark:shadow-none transition-all hover:-translate-y-1 active:scale-95"
          >
            <Printer size={22} className="group-hover:rotate-12 transition-transform" />
            طباعة / حفظ PDF
          </button>
        </div>

        {/* الكروت الإحصائية */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <StatCard icon={<Package/>} label="إجمالي الأدوية" value={stats.totalProducts} color="blue" />
          <StatCard icon={<DollarSign/>} label="إجمالي الأرباح" value={`${stats.totalSales.toLocaleString()} ل.س`} color="green" />
          <StatCard icon={<Users/>} label="عدد الطلبات" value={stats.ordersCount} color="purple" />
        </div>

        {/* المخططات البيانية */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white dark:bg-gray-800/50 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-gray-700/50 card-print">
            <h3 className="text-lg font-black text-gray-800 dark:text-gray-100 mb-8 flex items-center gap-2">
              <div className="w-2 h-6 bg-blue-500 rounded-full"></div> تحليل السيولة
            </h3>
            <div className="h-[300px]">
              <Line data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800/50 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-gray-700/50 card-print">
            <h3 className="text-lg font-black text-gray-800 dark:text-gray-100 mb-8 flex items-center gap-2">
              <div className="w-2 h-6 bg-emerald-500 rounded-full"></div> تكرار العمليات
            </h3>
            <div className="h-[300px]">
              <Bar 
                data={{
                  labels: chartData.labels,
                  datasets: [{ 
                    data: chartData.datasets[0]?.data, 
                    backgroundColor: '#10b981', 
                    borderRadius: 12,
                    barThickness: 25
                  }]
                }} 
                options={chartOptions} 
              />
            </div>
          </div>
        </div>

        {/* جدول إضافي يظهر في الطباعة فقط ليعطي التقرير تفاصيل أكثر */}
        <div className="bg-white dark:bg-gray-800/50 p-8 rounded-[40px] shadow-sm border border-gray-100 dark:border-gray-700/50 card-print">
          <h3 className="text-lg font-black text-gray-800 dark:text-gray-100 mb-6">سجل العمليات الأخيرة</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="py-4 text-gray-400 font-bold text-sm">رقم الطلب</th>
                  <th className="py-4 text-gray-400 font-bold text-sm text-right">الزبون</th>
                  <th className="py-4 text-gray-400 font-bold text-sm">المبلغ</th>
                  <th className="py-4 text-gray-400 font-bold text-sm">التاريخ</th>
                </tr>
              </thead>
              <tbody>
                {rawOrders.slice(0, 10).map((order) => (
                  <tr key={order.id} className="border-b border-gray-50 dark:border-gray-700/50">
                    <td className="py-4 font-bold text-gray-700 dark:text-gray-300">#{order.id}</td>
                    <td className="py-4 dark:text-gray-400">{order.customer_name || 'زبون عام'}</td>
                    <td className="py-4 font-black text-blue-600">{Number(order.total_price_syp).toLocaleString()} ل.س</td>
                    <td className="py-4 text-gray-400 text-sm">{new Date(order.created_at).toLocaleDateString('ar-SY')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const themes = {
    blue: "text-blue-600 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400",
    green: "text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-400",
    purple: "text-purple-600 bg-purple-50 dark:bg-purple-500/10 dark:text-purple-400"
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-8 rounded-[35px] shadow-sm border border-gray-100 dark:border-gray-700/50 flex items-center gap-6 card-print">
      <div className={`p-5 rounded-[25px] ${themes[color]}`}>
        {React.cloneElement(icon, { size: 32 })}
      </div>
      <div>
        <p className="text-gray-400 dark:text-gray-500 text-xs font-black uppercase tracking-widest">{label}</p>
        <p className="text-3xl font-black text-gray-900 dark:text-white mt-1">{value}</p>
      </div>
    </div>
  );
};

export default Stats;